import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentLanguage } from "../store/reducers/languageReducer";
import { getfeaturesectionApi } from "../store/actions/campaign";
import StyleOne from "./StyleOne";
import VideoPlayerModal from "./VideoPlayerModal";
import { SectionPanel, AdSpace } from "./SectionShared";
import { translate } from "../utils";
import Skeleton from "react-loading-skeleton";
import "./pt-home.css";

/*
 Homepage layout (bhagwa design):
   1. Featured hero slider + Latest News  (first section that has news)
   2. `afterFeatured` (shortcut cards from HomePage)
   3. All other sections as saffron-header panels in a grid:
        rows of 2 (7 + 5 columns) and rows of 3 (4 + 4 + 4)
      `extraPanels` (e.g. Photo Gallery) are added at the end of the grid.
*/

/* Section shown in the panel next to the hero slider (instead of "Latest News").
   Newspaper Coverage = feature section id 24 (same id as /view-all/24).
   SIDE_SECTION_ALSO_IN_GRID = true  -> it is ALSO shown as a big panel lower on the page. */
const SIDE_SECTION_ID = 24;
const SIDE_SECTION_ALSO_IN_GRID = false;
const isSideSection = (s) =>
    String(s?.id) === String(SIDE_SECTION_ID) || /newspaper|समाचार\s*पत्र|अखबार/i.test(String(s?.title || ""));

const hasContent = (s) => s && (s.news?.length || s.videos?.length || s.breaking_news?.length);
const kindOf = (s) => (s.videos?.length ? "video" : "news");

const assignSpans = (panels) => {
    const out = [];
    let i = 0;
    let rowSize = 2;
    while (i < panels.length) {
        const row = panels.slice(i, i + rowSize);
        if (row.length === 1) {
            out.push({ ...row[0], span: 12 });
        } else if (row.length === 2) {
            const [a, b] = row;
            if (a.kind === b.kind) {
                out.push({ ...a, span: 6 }, { ...b, span: 6 });
            } else {
                out.push({ ...a, span: a.kind === "video" ? 5 : 7 }, { ...b, span: b.kind === "video" ? 5 : 7 });
            }
        } else {
            row.forEach((p) => out.push({ ...p, span: 4 }));
        }
        i += rowSize;
        rowSize = rowSize === 2 ? 3 : 2;
    }
    return out;
};

const FeatureLayout = ({ afterFeatured = null, extraPanels = [] }) => {
    const [Data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [video, setVideo] = useState({ url: null, type: null, show: false });

    const currentLanguage = useSelector(selectCurrentLanguage);

    useEffect(() => {
        setIsLoading(true);
        getfeaturesectionApi(
            (response) => {
                setData(Array.isArray(response?.data) ? response.data : []);
                setIsLoading(false);
            },
            () => {
                setData([]);
                setIsLoading(false);
            }
        );
    }, [currentLanguage]);

    const playVideo = (url, type = null) => setVideo({ url, type, show: true });

    if (isLoading) {
        return (
            <div className="container loading_data py-4">
                <Skeleton height={360} />
                <Skeleton height={20} count={6} className="mt-2" />
            </div>
        );
    }

    const sections = Data.filter(hasContent);
    const sideSection = sections.find((s) => isSideSection(s) && (s.news?.length || s.breaking_news?.length)) || null;
    const featuredIndex = sections.findIndex((s) => s.news?.length > 0 && s !== sideSection);
    const featured = featuredIndex >= 0 ? sections[featuredIndex] : null;
    const others = sections.filter(
        (s, i) => i !== featuredIndex && (SIDE_SECTION_ALSO_IN_GRID || s !== sideSection)
    );

    const panels = assignSpans([
        ...others.map((s) => ({
            key: `${s.news_type}-${s.id}`,
            kind: kindOf(s),
            node: (
                <>
                    <AdSpace Data={s} type={s.news_type} />
                    <SectionPanel Data={s} onPlayVideo={playVideo} />
                </>
            ),
        })),
        ...extraPanels.filter(Boolean),
    ]);

    if (!featured && panels.length === 0) {
        return (
            <>
                {afterFeatured}
                <p className="no_data_available">{translate("noNews")}</p>
            </>
        );
    }

    return (
        <>
            {featured ? <StyleOne isLoading={false} Data={featured} sideSection={sideSection} /> : null}

            {afterFeatured}

            {panels.length > 0 ? (
                <section className="pt-home-panels">
                    <div className="container">
                        <div className="pt-panels-grid">
                            {panels.map((p) => (
                                <div key={p.key} className={`pt-panel-cell pt-span-${p.span}`}>
                                    {p.node}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            <VideoPlayerModal
                show={video.show}
                onHide={() => setVideo((v) => ({ ...v, show: false }))}
                keyboard
                url={video.url}
                type_url={video.type}
            />
        </>
    );
};

export default FeatureLayout;
